import { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarIcon, CaretLeftIcon, CaretRightIcon, CaretDownIcon } from "@phosphor-icons/react";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  isWithinInterval,
  Locale,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { de, enUS, es, fr, ja } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { getPresets } from "../../shared/constants";
import { IDateRangePreset } from "../../interfaces";
import { useAuth, useIsMobile } from "../../hooks";

const localeMap: Record<string, Locale> = { en: enUS, es, de, fr, ja };

interface DateRangePickerButtonProps {
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onPresetChange?: (preset: IDateRangePreset | undefined) => void;
  initialStartDate?: Date;
  initialEndDate?: Date | null;
  initialPreset?: string;
  allowTodaySelection?: boolean;
}

// ── Calendar grid builder ────────────────────────────────────────────────────

interface DayCell {
  date: Date;
  outOfMonth: boolean;
}

function buildCalendarCells(month: Date): DayCell[] {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  const days = eachDayOfInterval({ start, end });
  const leadingCount = start.getDay();
  // Always 42 cells (6 rows × 7 cols) — keeps the grid height identical every month
  const trailingCount = 42 - (leadingCount + days.length);

  return [
    ...Array.from({ length: leadingCount }, (_, i) => ({
      date: subDays(start, leadingCount - i),
      outOfMonth: true,
    })),
    ...days.map((d) => ({ date: d, outOfMonth: false })),
    ...Array.from({ length: trailingCount }, (_, i) => ({
      date: addDays(end, i + 1),
      outOfMonth: true,
    })),
  ];
}

// ── Component ────────────────────────────────────────────────────────────────

const DateRangePickerButton: React.FC<DateRangePickerButtonProps> = ({
  onDateRangeChange,
  onPresetChange,
  initialStartDate,
  initialEndDate,
  initialPreset,
  allowTodaySelection = false,
}) => {
  const { t, i18n } = useTranslation();
  const { isVip, subscriptionDetails } = useAuth();
  const isMobile = useIsMobile();
  const locale = localeMap[i18n.language] || localeMap.en;
  const maxPastDays =
    !isVip && subscriptionDetails?.live_dashboard_window_days != null
      ? subscriptionDetails.live_dashboard_window_days
      : undefined;

  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);
  const presets = getPresets(t);
  const earliestAllowed = maxPastDays != null ? subDays(today, maxPastDays) : null;

  // ── State ──────────────────────────────────────────────────────────────────

  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(
    initialStartDate || subDays(today, 29)
  );
  const [endDate, setEndDate] = useState<Date | null>(
    initialEndDate !== undefined ? initialEndDate : today
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    initialStartDate || yesterday
  );
  const [selectedPreset, setSelectedPreset] = useState(initialPreset || "");
  const [navDir, setNavDir] = useState<1 | -1>(1);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, flipUp: false });
  const [triggerRect, setTriggerRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const snapshotRef = useRef<{ startDate: Date | null; endDate: Date | null; preset: string } | null>(null);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const isDisabled = useCallback(
    (date: Date) => {
      const d = startOfDay(date);
      if (earliestAllowed && isBefore(d, earliestAllowed)) return true;
      if (allowTodaySelection) return isBefore(today, d);
      return !isBefore(d, today);
    },
    [earliestAllowed, allowTodaySelection, today]
  );

  const isPresetDisabled = useCallback(
    (preset: IDateRangePreset) => {
      if (!earliestAllowed) return false;
      const [s] = preset.getRange();
      return isBefore(startOfDay(s), earliestAllowed);
    },
    [earliestAllowed]
  );

  const validRange = useCallback(
    (s: Date, e: Date) => !isDisabled(s) && !isDisabled(e) && !isBefore(e, s),
    [isDisabled]
  );

  const fmt = (d: Date) => format(d, "MMM d, yyyy", { locale });

  // ── Dropdown position ──────────────────────────────────────────────────────

  const computePos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setTriggerRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    const W = 820;
    const H = 480;
    const gap = 6;
    const flipUp = r.bottom + gap + H > window.innerHeight;
    const top = flipUp ? r.top - gap - H : r.bottom + gap;
    const left = Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8));
    setDropPos({ top, left, flipUp });
  }, [setTriggerRect]);

  const open = () => {
    snapshotRef.current = { startDate, endDate, preset: selectedPreset };
    computePos();
    setIsOpen(true);
  };

  const cancel = useCallback(() => {
    const snap = snapshotRef.current;
    if (snap) {
      setStartDate(snap.startDate);
      setEndDate(snap.endDate);
      setSelectedPreset(snap.preset);
      if (snap.preset) setCurrentMonth(snap.startDate ?? yesterday);
    }
    setIsOpen(false);
  }, [yesterday]);

  const close = () => setIsOpen(false);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!dropdownRef.current?.contains(t) && !triggerRef.current?.contains(t)) {
        cancel();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, cancel]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") cancel(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, cancel]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const selectPreset = (id: string) => {
    if (id === "custom") {
      setSelectedPreset("custom");
      return;
    }
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    const [s, e] = preset.getRange();
    if (!validRange(s, e)) return;
    setStartDate(s);
    setEndDate(e);
    setSelectedPreset(id);
    setCurrentMonth(s);
  };

  const clickDay = (date: Date) => {
    if (isDisabled(date)) return;
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setSelectedPreset("custom");
    } else {
      let s = startDate, e = date;
      if (isBefore(date, startDate)) { s = date; e = startDate; }
      if (!validRange(s, e)) return;
      setStartDate(s);
      setEndDate(e);
      setSelectedPreset("custom");
    }
  };

  const apply = () => {
    if (!startDate || !endDate || !validRange(startDate, endDate)) return;
    onDateRangeChange?.(startDate, endDate);
    if (selectedPreset === "custom") {
      const s = startDate, e = endDate;
      onPresetChange?.({ id: "custom", label: t("common.custom", "Custom"), getRange: () => [s, e] });
    } else {
      const preset = presets.find((p) => p.id === selectedPreset);
      onPresetChange?.(preset);
    }
    close();
  };

  const navigate = (dir: 1 | -1) => {
    setNavDir(dir);
    setCurrentMonth((m) => (dir === 1 ? addMonths(m, 1) : subMonths(m, 1)));
  };

  // ── Calendar grid ──────────────────────────────────────────────────────────

  const renderMonth = (monthOffset: number) => {
    const month = addMonths(currentMonth, monthOffset);
    const cells = buildCalendarCells(month);
    const weekDays = Array.from({ length: 7 }, (_, i) =>
      format(addDays(startOfWeek(new Date()), i), "EEEEE", { locale })
    );

    return (
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold text-gray-800 dark:text-gray-100 text-center mb-4 tracking-tight">
          {format(month, "MMMM yyyy", { locale })}
        </p>

        {/* Week day headers */}
        <div className="grid grid-cols-7 mb-1">
          {weekDays.map((d, i) => (
            <div key={i} className="text-xs font-semibold text-gray-400 dark:text-neutral-500 text-center py-1.5">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {cells.map(({ date, outOfMonth }, idx) => {
            if (outOfMonth) return <div key={idx} className="h-11" />;

            const disabled = isDisabled(date);
            const isStart = startDate ? isSameDay(date, startDate) : false;
            const isEnd = endDate ? isSameDay(date, endDate) : false;
            const inRange =
              startDate && endDate
                ? isWithinInterval(date, { start: startDate, end: endDate }) && !isStart && !isEnd
                : false;
            const isToday = isSameDay(date, today);
            const hasRange = !!(startDate && endDate);

            return (
              <div key={idx} className="relative h-10 flex items-center justify-center">
                {/* Range background strip — continuous fill between start & end */}
                {hasRange && (isStart || isEnd || inRange) && (
                  <div
                    className={`absolute inset-y-1 bg-indigo-50 dark:bg-indigo-500/20 ${isStart
                      ? "left-1/2 right-0"
                      : isEnd
                        ? "left-0 right-1/2"
                        : "inset-x-0"
                      }`}
                  />
                )}

                <button
                  onClick={() => clickDay(date)}
                  disabled={disabled}
                  className={[
                    // Force perfect square so border-radius: 50% always produces a circle
                    "relative z-10 shrink-0 w-9 h-9 rounded-full text-sm",
                    "flex items-center justify-center",
                    "transition-colors duration-100 select-none outline-none",
                    disabled ? "text-gray-200 dark:text-neutral-600 cursor-not-allowed" : "cursor-pointer",
                    isStart || isEnd
                      ? "bg-indigo-600 text-white font-bold shadow-md shadow-indigo-200 dark:shadow-indigo-900"
                      : "",
                    inRange && !disabled
                      ? "text-indigo-700 dark:text-indigo-300 font-medium hover:bg-indigo-100 dark:hover:bg-indigo-500/20"
                      : "",
                    !isStart && !isEnd && !inRange && !disabled
                      ? "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-neutral-700"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {format(date, "d")}
                  {/* Today dot — small indicator below the number, no ring */}
                  {isToday && !isStart && !isEnd && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-400" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Trigger label ──────────────────────────────────────────────────────────

  const triggerLabel =
    startDate && endDate
      ? `${fmt(startDate)} – ${fmt(endDate)}`
      : t("common.selectDateRange", "Select date range");

  // ── Mobile (inline, no popover) ────────────────────────────────────────────

  if (isMobile) {
    return (
      <div className="w-full space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {presets.filter((p) => p.id !== "custom").map((preset) => {
            const dis = isPresetDisabled(preset);
            return (
              <button
                key={preset.id}
                disabled={dis}
                onClick={() => !dis && selectPreset(preset.id)}
                className={[
                  "px-3 py-2 rounded-xl text-sm font-medium transition-all",
                  selectedPreset === preset.id
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-neutral-600",
                  dis ? "opacity-40 cursor-not-allowed" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-500 dark:text-gray-400 transition-colors"
          >
            <CaretLeftIcon size={16} weight="bold" />
          </button>
          <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
            {format(currentMonth, "MMMM yyyy", { locale })}
          </span>
          <button
            onClick={() => navigate(1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-500 dark:text-gray-400 transition-colors"
          >
            <CaretRightIcon size={16} weight="bold" />
          </button>
        </div>

        {renderMonth(0)}

        <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-neutral-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {startDate && endDate ? (
              <span className="font-semibold text-gray-800 dark:text-gray-100">{fmt(startDate)} – {fmt(endDate)}</span>
            ) : startDate ? (
              <span>{fmt(startDate)} – <span className="text-gray-400 dark:text-neutral-500">select end</span></span>
            ) : null}
          </span>
          <button
            onClick={apply}
            disabled={!startDate || !endDate}
            className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors"
          >
            {t("common.apply", "Apply")}
          </button>
        </div>
      </div>
    );
  }

  // ── Desktop ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Trigger button — sits above the backdrop when open */}
      <button
        ref={triggerRef}
        onClick={() => (isOpen ? close() : open())}
        style={{ position: "relative" }}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-700 shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-800 whitespace-nowrap hover:bg-gray-50 dark:hover:bg-neutral-700 focus:outline-none min-w-[280px]"
      >
        <CalendarIcon size={15} className="text-gray-400 shrink-0" />
        <span className="flex-1 text-left">{triggerLabel}</span>
        <CaretDownIcon
          size={12}
          weight="bold"
          className={`transition-transform duration-200 text-gray-400 shrink-0 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* Portal: backdrop + trigger clone + dropdown
          Rendered at document.body so position:fixed is viewport-relative even inside
          a framer-motion modal that creates a CSS transform stacking context. The trigger
          clone is positioned exactly over the real button at z-index above the backdrop
          so only the picker and the trigger remain sharp. */}
      {ReactDOM.createPortal(
        <>
          {/* Full-screen blur backdrop */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                style={{ position: "fixed", inset: 0, zIndex: 99990 }}
                className="bg-gray-900/30 backdrop-blur-sm"
              />
            )}
          </AnimatePresence>

          {/* Trigger clone — floats above the backdrop at the exact trigger location */}
          {isOpen && triggerRect && (
            <button
              onClick={cancel}
              style={{
                position: "fixed",
                top: triggerRect.top,
                left: triggerRect.left,
                width: triggerRect.width,
                height: triggerRect.height,
                zIndex: 99995,
              }}
              className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-700 shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-neutral-800 whitespace-nowrap hover:bg-gray-50 dark:hover:bg-neutral-700 focus:outline-none"
            >
              <CalendarIcon size={15} className="text-gray-400 shrink-0" />
              <span className="flex-1 text-left">{triggerLabel}</span>
              <CaretDownIcon size={12} weight="bold" className="rotate-180 text-gray-400 shrink-0" />
            </button>
          )}

          <AnimatePresence>
            {isOpen && (
              <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: dropPos.flipUp ? 6 : -6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: dropPos.flipUp ? 6 : -6, scale: 0.97 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                style={{ position: "fixed", top: dropPos.top, left: dropPos.left, zIndex: 99999 }}
                className="w-[820px] bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-neutral-700 overflow-hidden"
              >
                <div className="flex">

                  {/* ── Presets sidebar ── */}
                  <div className="w-40 shrink-0 border-r border-gray-100 dark:border-neutral-700 py-3 px-2 flex flex-col gap-0.5 bg-gray-50/60 dark:bg-neutral-800/60">
                    <p className="text-[10px] font-bold text-gray-400 dark:text-neutral-500 uppercase tracking-widest px-3 pt-1 pb-1.5">
                      Quick select
                    </p>
                    {presets.filter((p) => p.id !== "custom").map((preset) => {
                      const dis = isPresetDisabled(preset);
                      const active = selectedPreset === preset.id;
                      return (
                        <button
                          key={preset.id}
                          disabled={dis}
                          onClick={() => !dis && selectPreset(preset.id)}
                          className={[
                            "w-full text-left px-3 py-2 rounded-xl text-sm transition-all duration-100",
                            active
                              ? "bg-indigo-600 text-white font-semibold shadow-sm"
                              : dis
                                ? "text-gray-300 dark:text-neutral-600 cursor-not-allowed"
                                : "text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-neutral-700 hover:text-gray-900 dark:hover:text-gray-100 hover:shadow-sm",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                    <div className="mt-1 pt-1 border-t border-gray-100 dark:border-neutral-700">
                      <button
                        onClick={() => selectPreset("custom")}
                        className={[
                          "w-full text-left px-3 py-2 rounded-xl text-sm transition-all duration-100",
                          selectedPreset === "custom"
                            ? "bg-indigo-600 text-white font-semibold shadow-sm"
                            : "text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-neutral-700 hover:text-gray-900 dark:hover:text-gray-100 hover:shadow-sm",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {t("common.custom", "Custom")}
                      </button>
                    </div>
                  </div>

                  {/* ── Calendar area ── */}
                  <div className="flex-1 flex flex-col min-w-0">
                    <div className="pt-5 px-2 flex-1">
                      {/* Month navigation */}
                      <div className="flex items-center gap-2 mb-4">
                        <button
                          onClick={() => navigate(-1)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-400 dark:text-neutral-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0"
                        >
                          <CaretLeftIcon size={16} weight="bold" />
                        </button>

                        {/* Sliding calendar months — fixed height prevents layout shift */}
                        <div className="flex-1 overflow-hidden" style={{ height: 308 }}>
                          <AnimatePresence mode="wait" initial={false}>
                            <motion.div
                              key={currentMonth.toISOString()}
                              initial={{ opacity: 0, x: navDir * 32 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: navDir * -32 }}
                              transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}
                              className="flex gap-10"
                            >
                              {renderMonth(0)}
                              {renderMonth(1)}
                            </motion.div>
                          </AnimatePresence>
                        </div>

                        <button
                          onClick={() => navigate(1)}
                          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-400 dark:text-neutral-500 hover:text-gray-700 dark:hover:text-gray-200 transition-colors shrink-0"
                        >
                          <CaretRightIcon size={16} weight="bold" />
                        </button>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-100 dark:border-neutral-700 px-6 py-4 flex items-center justify-between bg-gray-50/40 dark:bg-neutral-800/40">
                      <span className="text-sm">
                        {startDate && endDate ? (
                          <span className="font-semibold text-gray-800 dark:text-gray-100">
                            {fmt(startDate)}{" "}
                            <span className="text-gray-400 dark:text-neutral-500 font-normal">–</span>{" "}
                            {fmt(endDate)}
                          </span>
                        ) : startDate ? (
                          <span className="text-gray-500 dark:text-gray-400">
                            {fmt(startDate)}{" "}
                            <span className="text-gray-300 dark:text-neutral-600">– select end date</span>
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-neutral-500">{t("common.selectDateRange", "Select date range")}</span>
                        )}
                      </span>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={cancel}
                          className="px-5 py-2 rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                          {t("common.cancel", "Cancel")}
                        </button>
                        <button
                          onClick={apply}
                          disabled={!startDate || !endDate}
                          className="px-6 py-2 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {t("common.apply", "Apply")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>,
        document.body
      )}
    </>
  );
};

export default DateRangePickerButton;
